import { DataTable, useColumns } from "@/components/data-table";
import { DataFetcher } from "@/components/display";
import { useFilters } from "@/components/filters";
import { ListPage } from "@/components/list-page";
import { ConfirmDialog, Dialog, ModalHeader, ModalValueExtractor, useOpenModal } from "@/components/modal";
import useHasPermisos from "@/features/auth/hooks/useHasPermisos";
import { getAllClientes } from "@/features/auxiliar/clientes/api/get-all-clientes";
import { getCliente } from "@/features/auxiliar/clientes/api/get-cliente";
import { getAllVendedores } from "@/features/auxiliar/vendedores/api/get-all-vendedores";
import { getVendedor } from "@/features/auxiliar/vendedores/api/get-vendedor";
import CsvExportDialog from "@/features/export/components/csv-export-dialog";
import PrintExportDialog from "@/features/export/components/print-dialog-obs";
import XlsxExportDialog from "@/features/export/components/xlsx-export-dialog";
import { getAllArticulos } from "@/features/inventario/articulos/api/get-all-articulos";
import { getArticulo } from "@/features/inventario/articulos/api/get-articulo";
import { CancelVentaMutation } from "@/features/inventario/ventas/api/cancel-venta";
import { CancelVentaConFEMutation } from "@/features/inventario/ventas/api/cancel-venta-con-fe";
import { CancelPagoFEMutation } from "@/features/cajas/caja-actual/api/cancel-pago-fe";
import { CancelPagoMutation } from "@/features/cajas/caja-actual/api/cancel-pago";
import { DeleteVentaMutation } from "@/features/inventario/ventas/api/delete-venta";
import { getAllVentas, useGetAllVentasQuery } from "@/features/inventario/ventas/api/get-all-ventas";
import { GetVentaQuery } from "@/features/inventario/ventas/api/get-venta";
import { getVentaKude } from "@/features/inventario/ventas/api/get-venta-kude";
import { getVentaKudeTicketDatos } from "@/features/inventario/ventas/api/get-venta-kude-ticket-datos";
import VentaPrintForm from "@/features/inventario/ventas/components/venta-print-form";
import VentaView from "@/features/inventario/ventas/components/venta-view";
import KudeTicketView from "@/features/facturacion-electronica/components/kude-ticket-print/kude-ticket-view";
import useMonetizacion from "@/features/monetizacion/hooks/use-monetizacion";
import { useConfigurationContext } from "@/features/configuration/contexts/configuration-context";
import { usePrinter } from "@/components/print";
import useKudePrinter from "@/features/facturacion-electronica/hooks/use-kude-printer";
import { openPdfFromBase64 } from "@/features/facturacion-electronica/utils/pdf-utils";
import { formatDate, formatNumber, formatString } from "@/shared/formats";
import { notify } from "@/shared/notification";
import { Permiso } from "@/shared/types/permiso";
import { Venta } from "@/shared/types/venta";
import { useCargarPresupuestoEnNuevaVenta } from "@/features/inventario/ventas/hooks/use-cargar-presupuesto-en-nueva-venta";
import CancelIcon from '@mui/icons-material/Cancel';
import PrintIcon from '@mui/icons-material/Print';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { useState } from 'react';

export default function VentasPage() {
    const openModal = useOpenModal();
    const [printingVentaId, setPrintingVentaId] = useState<string | null>(null);
    const [cargandoPresupuestoId, setCargandoPresupuestoId] = useState<string | null>(null);
    const { monedaBase } = useMonetizacion();
    const { modeTicket, printPaperWidth } = useConfigurationContext();
    const cargarPresupuestoEnNuevaVenta = useCargarPresupuestoEnNuevaVenta();
    const printer = usePrinter();
    const { printKude, Printer: KudePrinter } = useKudePrinter();

    const handleCargarEnNuevaVenta = async (row: Venta) => {
        setCargandoPresupuestoId(row.id);
        try {
            await cargarPresupuestoEnNuevaVenta(row);
        } finally {
            setCargandoPresupuestoId(null);
        }
    };

    const handlePrintVenta = async (row: Venta) => {
        if (row.tieneDE) {
            try {
                setPrintingVentaId(row.id);
                if (modeTicket) {
                    const response = await getVentaKudeTicketDatos(row.id);
                    await printer.print({
                        documentTitle: `KUDE Venta N°${row.numeroVenta}`,
                        printComponent: <KudeTicketView datos={response.data} />,
                        pageStyle: `@page { size: ${printPaperWidth} auto; margin: 0; }`
                    });
                } else {
                    const response = await getVentaKude(row.id);
                    openPdfFromBase64(response.data.kudeBase64);
                }
                notify(`KUDE de la venta N°${row.numeroVenta} abierto correctamente.`, { variant: "success" });
            } catch (error) {
                notify("Error al descargar el KUDE.", { variant: "error" });
                console.error(error);
            } finally {
                setPrintingVentaId(null);
            }
        } else {
            openModal("printVenta", row.id);
        }
    };

    const hasWritePermiso = useHasPermisos(Permiso.WriteVentas);

    const filterData = useFilters({
        filters: [
            { type: "dateRange", label: "Fecha", fromName: "desde", toName: "hasta" },
            { type: "string", label: "N° de Venta", name: "numeroVenta" },
            { type: "string", label: "N° de Comprobante", name: "numComprobante" },
            { type: "model", label: "Cliente", name: "clienteId", get: getCliente, getAll: getAllClientes },
            { type: "model", label: "Vendedor", name: "vendedorId", get: getVendedor, getAll: getAllVendedores },
            { type: "model", label: "Artículo", name: "articuloId", get: getArticulo, getAll: getAllArticulos },
            { type: "select", label: "Documento Electrónico", name: "tieneDE", options: [{ label: "Todos", value: "" }, { label: "Con DE", value: true }, { label: "Sin DE", value: false }] },
            { type: "pagination" }
        ]
    });

    const fetchData = useGetAllVentasQuery(filterData.filterData);

    const columnData = useColumns<Venta>({
        columns: [
            { key: "numVenta", header: "N°", render: row => formatString(row.numeroVenta), width: "auto" },
            { key: "fecha", header: "Fecha", render: row => formatDate(row.fecha), width: "auto" },
            { key: "numComprobante", header: "N° de Comprobante", render: row => formatString(row.numComprobante), show: false },
            { key: "cliente", header: "Cliente", render: row => formatString(row.clienteNombre) },
            { key: "vendedor", header: "Vendedor", render: row => formatString(row.vendedorNombre) },
            { key: "condicion", header: "Condición", render: row => row.credito ? "Crédito" : "Contado", width: "auto", align: "center" },
            { key: "estado", header: "Estado", render: row => formatString(row.anulado ? "Anulado" : row.pagado ? "Pagado" : row.presupuesto ? "Presupuesto" : "Pendiente"), width: "auto", align: "center" },
            { key: "tieneDE", header: "Tiene DE", render: row => formatString(row.tieneDE ? "Sí" : "No"), show: false, width: "auto", },
            { key: "parcialExenta", header: `Parcial Excenta (${monedaBase.simbolo})`, render: row => formatNumber(row.parcialExenta), align: "right", show: false, width: "auto" },
            { key: "parcialIvaCinco", header: `Parcial 5% (${monedaBase.simbolo})`, render: row => formatNumber(row.parcialIvaCinco), align: "right", show: false, width: "auto" },
            { key: "parcialIvaDiez", header: `Parcial 10% (${monedaBase.simbolo})`, render: row => formatNumber(row.parcialIvaDiez), align: "right", show: false, width: "auto" },
            { key: "total", header: `Total (${monedaBase.simbolo})`, render: row => formatNumber(row.total), align: "right", width: "auto" },
            { key: "utilidad", header: `Utilidad (${monedaBase.simbolo})`, render: row => formatNumber(row.utilidad), align: "right", width: "auto" },
            { key: "liquidacionIvaCinco", header: `IVA 5% (${monedaBase.simbolo})`, render: row => formatNumber(row.liquidacionIvaCinco), align: "right", show: false },
            { key: "liquidacionIvaDiez", header: `IVA 10% (${monedaBase.simbolo})`, render: row => formatNumber(row.liquidacionIvaDiez), align: "right", show: false },
            { key: "liquidacionIvaTotal", header: `IVA Total (${monedaBase.simbolo})`, render: row => formatNumber(row.liquidacionIvaTotal), align: "right", show: false },
            { key: "creadoPor", header: "Creado Por", render: row => formatString(row.creadoPor), show: false },
            { key: "creacion", header: "Fecha de Creación", render: row => formatDate(row.fechaCreacion), align: "center", show: false },
            { key: "modificadoPor", header: "Modificado Por", render: row => formatString(row.modificadoPor), show: false },
            { key: "modificacion", header: "Última Modificación", render: row => formatDate(row.fechaModificacion), align: "center", show: false }
        ],
        key: "ventas"
    });

    return <ListPage>
        <ListPage.Header>
            <ListPage.Header.Actions title="Ventas">
                <ListPage.Header.Actions.FilterButton />
                <ListPage.Header.Actions.ColumnButton columnData={columnData} />
                <ListPage.Header.Actions.Divider />
                <ListPage.Header.Actions.ExportButton onClick={() => openModal("print")}>
                    <ListPage.Header.Actions.ExportButton.Print onClick={() => openModal("print")} />
                    <ListPage.Header.Actions.ExportButton.Csv onClick={() => openModal("csv")} />
                    <ListPage.Header.Actions.ExportButton.Xlsx onClick={() => openModal("xlsx")} />
                </ListPage.Header.Actions.ExportButton>
            </ListPage.Header.Actions>
            <ListPage.Header.Filters filterData={filterData} />
        </ListPage.Header>
        <DataTable flex={1}>
            <DataTable.Content
                rows={fetchData.data?.result}
                columns={[
                    ...columnData.columns,
                    {
                        header: "Acción", width: "auto", align: "center", render: row => <DataTable.EntryOption>
                            <DataTable.EntryOption.ViewButton onClick={() => openModal("view", row.id)} />
                            <DataTable.EntryOption.Button Icon={PrintIcon} title="Imprimir" onClick={() => handlePrintVenta(row)} disabled={printingVentaId === row.id} />
                            {row.presupuesto && (
                                <DataTable.EntryOption.Button
                                    Icon={ShoppingCartIcon}
                                    title="Cargar en Nueva Venta"
                                    onClick={() => handleCargarEnNuevaVenta(row)}
                                    disabled={cargandoPresupuestoId === row.id}
                                />
                            )}
                            {row.pagado && hasWritePermiso && !row.anulado && (
                                row.tieneDE ? (
                                    <DataTable.EntryOption.Button Icon={CancelIcon} title="Anular Venta FE" onClick={() => openModal("cancelPagoFE", row)} />
                                ) : (
                                    <DataTable.EntryOption.Button Icon={CancelIcon} title="Anular Venta" onClick={() => openModal("cancelPago", row)} />
                                )
                            )}
                            {!row.pagado && hasWritePermiso && (!(row.anulado || row.presupuesto) ?
                                <DataTable.EntryOption.Button Icon={CancelIcon} title="Anular" onClick={() => openModal("cancel", row)} /> :
                                <DataTable.EntryOption.DeleteButton disabled={row.protegido} onClick={() => openModal("delete", row)} />)}
                        </DataTable.EntryOption>
                    }
                ]}
                fit="flex"
                loading={fetchData.isFetching}
                error={fetchData.error}
            />
            <DataTable.Pagination filterData={filterData} queryResult={fetchData} />
        </DataTable>

        <Dialog code="view" size="md" fullHeight>
            <ModalHeader>Ver Venta</ModalHeader>
            <ModalValueExtractor>
                {ventaId => <GetVentaQuery id={ventaId}>
                    {query => <DataFetcher query={query} flex={1}>
                        {venta => <VentaView venta={venta} onPrintButtonClick={() => openModal("printVenta", venta.id)} />}
                    </DataFetcher>}
                </GetVentaQuery>}
            </ModalValueExtractor>
        </Dialog>

        <Dialog code="printVenta" size="xs">
            <ModalValueExtractor>
                {ventaId => <GetVentaQuery id={ventaId}>
                    {query => <DataFetcher query={query} loadMode="loading">
                        {venta => <>
                            <ModalHeader>Imprimir Venta N°{venta.numeroVenta}</ModalHeader>
                            <VentaPrintForm venta={venta} padding={3} />
                        </>}
                    </DataFetcher>}
                </GetVentaQuery>}
            </ModalValueExtractor>
        </Dialog>

        <ConfirmDialog code="cancelPagoFE">
            <ModalValueExtractor>
                {venta => <CancelPagoFEMutation ventaId={venta.id}>
                    {mutation => <ConfirmDialog.Content
                        title={`Anular Pago de Venta N°${venta.numeroVenta}`}
                        description={`¿Estás seguro que quieres anular el pago de la venta N°${venta.numeroVenta}?, si la fecha de emision es mayor a 48 horas se generará una Nota de Crédito Electrónica.`}
                        onConfirm={async () => {
                            const result = await mutation.mutateAsync({});

                            if (result.data.nce) {
                                await printKude({ documentTitle: `KUDE N°${result.data.nce.docNumDE}`, kudeBase64: result.data.nce.kuDEBase64, deId: result.data.nce.idDE });
                                notify(`El pago de la venta N°${venta.numeroVenta} se ha anulado y se generó la Nota de Crédito Electrónica N°${result.data.nce.docNumDE}.`, { variant: "success" });
                            } else {
                                notify(`El pago de la venta N°${venta.numeroVenta} se ha anulado correctamente.`, { variant: "success" });
                            }
                        }}
                    />}
                </CancelPagoFEMutation>}
            </ModalValueExtractor>
        </ConfirmDialog>

        <ConfirmDialog code="cancelPago">
            <ModalValueExtractor>
                {venta => <CancelPagoMutation ventaId={venta.id}>
                    {mutation => <ConfirmDialog.Content
                        title={`Anular Pago de Venta N°${venta.numeroVenta}`}
                        description={`¿Estás seguro que quieres anular el pago de la venta N°${venta.numeroVenta}?`}
                        onConfirm={async () => {
                            await mutation.mutateAsync({});
                            notify(`El pago de la venta N°${venta.numeroVenta} se ha anulado correctamente.`, { variant: "success" });
                        }}
                    />}
                </CancelPagoMutation>}
            </ModalValueExtractor>
        </ConfirmDialog>

        <ConfirmDialog code="cancel">
            <ModalValueExtractor>
                {venta => venta.tieneDE && venta.credito ? (
                    <CancelVentaConFEMutation id={venta.id}>
                        {mutation => <ConfirmDialog.Content
                            title={`Anular Venta N°${venta.numeroVenta}`}
                            description={`¿Estás seguro que quieres anular la venta N°${venta.numeroVenta}?, si la fecha de emision es mayor a 48 horas se generará una Nota de Crédito Electrónica.`}
                            onConfirm={async () => {
                                const result = await mutation.mutateAsync({});
                                const nce = result?.nceGenerada;

                                if (nce) {
                                    await printKude({ documentTitle: `KUDE N°${nce.numeroDE}`, kudeBase64: nce.kudeBase64, deId: nce.idDE });
                                    notify(`La venta N°${venta.numeroVenta} se ha anulado correctamente. Se ha generado una Nota de Crédito Electrónica N°${nce.numeroDE}.`, { variant: "success" });
                                } else {
                                    notify(`La venta N°${venta.numeroVenta} se ha anulado correctamente. La factura electrónica ha sido cancelada/inutilizada.`, { variant: "success" });
                                }
                            }}
                        />}
                    </CancelVentaConFEMutation>
                ) : (
                    <CancelVentaMutation id={venta.id}>
                        {mutation => <ConfirmDialog.Content
                            title={`Anular Venta N°${venta.numeroVenta}`}
                            description={`¿Estás seguro que quieres anular la venta N°${venta.numeroVenta}?`}
                            onConfirm={async () => {
                                await mutation.mutateAsync({});
                                notify(`La venta N°${venta.numeroVenta} se ha anulado correctamente.`, { variant: "success" });
                            }}
                        />}
                    </CancelVentaMutation>
                )}
            </ModalValueExtractor>
        </ConfirmDialog>

        <ConfirmDialog code="delete">
            <ModalValueExtractor>
                {venta => <DeleteVentaMutation id={venta.id}>
                    {mutation => <ConfirmDialog.Content
                        title={`Borrar Venta N°${venta.numeroVenta}`}
                        description={`¿Estás seguro que quieres borrar la venta N°${venta.numeroVenta}?`}
                        onConfirm={async () => {
                            await mutation.mutateAsync({});
                            notify(`La venta N°${venta.numeroVenta} se ha borrardo correctamente.`, { variant: "success" });
                        }}
                    />}
                </DeleteVentaMutation>}
            </ModalValueExtractor>
        </ConfirmDialog>

        <PrintExportDialog
            code="print"
            columns={columnData.columns}
            filterData={filterData}
            fetchFn={getAllVentas}
            defaultFileTitle="Ventas"
        />

        <CsvExportDialog
            code="csv"
            columns={columnData.columns}
            filterData={filterData}
            fetchFn={getAllVentas}
            defaultFileTitle="Ventas"
        />

        <XlsxExportDialog
            code="xlsx"
            columns={columnData.columns}
            filterData={filterData}
            fetchFn={getAllVentas}
            defaultFileTitle="Ventas"
        />
        <printer.Printer />
        <KudePrinter />
    </ListPage>
}